import { CURRENCIES } from "../constants/data";
import { UI, FONT } from "../constants/tokens";
import { useFxRates } from "../utils/fxRates";
import ShekelSign, { HIDDEN_TEXT } from "./ShekelSign";

// ─── Every amount of money in the app (sprint 17) ────────────────────────────
// `Amount` draws a figure that is already in its currency; `Money` takes
// dollars, converts them to the display currency at the live rate, and draws
// that. Nothing else in the app types a "$" or a "₪" next to a number.
//
// The rules, one set for every currency (DECISIONS, sprint 17):
//   · the currency sign is 70% of the digits' size (never under 10px), in the same weight and the
//     same colour, sitting on the line's baseline · never raised, never at
//     full height, never faded. The ₪ is drawn (ShekelSign) at the height a
//     "$" has at that size, so "$7,255" and "₪7,255" stand the same.
//   · a sign is glued to the currency: "+$7,255", "−₪1,373". Minus is "−"
//     (U+2212). A figure that rounds to zero carries no sign.
//   · decimals: shekels whole; dollars, euros and pounds to the cent, and
//     whole from 100,000 or when the caller asks (`whole`, the portfolio's
//     total). The cents are 70% of the digits' size, same colour.
export const CUR_RATIO = 0.7;
// The currency sign is never smaller than this: 70% of an 11px secondary
// figure ("+₪805") is under 8px, a speck. Below ~14px the sign is a little
// over 70% of the digits instead (sprint 17, David).
export const CUR_MIN_PX = 10;
const BIG = 100000;

export function Amount({
  // size: px, or null to take the size of the text around it (in running
  // text): the digits at 1em, the currency and the cents at 0.7em.
  value, currency = "USD", size = 24, weight = 600, color, className, style,
  signed = false, whole = false, decimals = null, shekelFace = false,
  // suffix: a unit after the digits, at their size · "$1.2B", "$350M".
  suffix = null,
  letterSpacing = "-0.01em", ...rest
}) {
  const isILS = currency === "ILS";
  const x = Number(value) || 0;
  const places = decimals ?? (isILS || whole || Math.abs(x) >= BIG ? 0 : 2);
  // `whole` floors (a total never shows more than it holds); otherwise round.
  const shown = places === 0 && whole ? Math.trunc(x) : x;
  const str = Math.abs(shown).toLocaleString("en-US", { minimumFractionDigits: places, maximumFractionDigits: places });
  const zero = !/[1-9]/.test(str);
  const neg = shown < 0 && !zero;
  const sign = neg ? "−" : signed && !zero && shown > 0 ? "+" : "";
  const dot = str.lastIndexOf(".");
  const intPart = dot === -1 ? str : str.slice(0, dot);
  const fracPart = dot === -1 ? null : str.slice(dot);
  const inherit = size == null;
  const full = inherit ? "1em" : size;
  const small = inherit ? `${CUR_RATIO}em` : Math.round(size * CUR_RATIO);
  const sign_px = inherit ? `max(${CUR_MIN_PX}px, ${CUR_RATIO}em)` : Math.max(CUR_MIN_PX, Math.round(size * CUR_RATIO));
  const sym = (CURRENCIES[currency] || CURRENCIES.USD).symbol;
  return (
    <span dir="ltr" className={["si-num", shekelFace && "si-shekel", className].filter(Boolean).join(" ")}
      data-amount={currency} {...rest}
      style={{
        // Inline, not inline-flex: flex items copy as separate lines ("+\n₪\n1,307").
        // Inline runs of different sizes sit on the shared baseline anyway.
        unicodeBidi: "isolate", display: "inline", whiteSpace: "nowrap",
        fontFamily: FONT.num, fontWeight: weight ?? "inherit", color: color || (inherit ? "inherit" : UI.text.primary),
        letterSpacing, fontVariantNumeric: "tabular-nums", lineHeight: 1.1, ...style,
      }}>
      {sign && <span style={{ fontSize: full }}>{sign}</span>}
      <span data-cur style={{ fontSize: sign_px }}>
        {/* The drawn ₪ is named for a screen reader; the zero-size "₪" is
            there for copying only, and hidden from it (VoiceOver drops
            zero-size text anyway · review). */}
        {isILS ? <><ShekelSign title="₪"/><span aria-hidden="true" style={HIDDEN_TEXT}>₪</span></> : sym}
      </span>
      <span style={{ fontSize: full }}>{intPart}</span>
      {fracPart && <span style={{ fontSize: small }}>{fracPart}</span>}
      {suffix && <span style={{ fontSize: full }}>{suffix}</span>}
    </span>
  );
}

export default function Money({ value, cur = "USD", size = 24, weight = 600, color, className, style, whole = false, signed = false, decimals = null, letterSpacing }) {
  const fx = useFxRates()[cur];
  // No live rate for this currency: the figure does not exist, and a dash in
  // the same slot says so without pretending to be a total.
  if (!fx) {
    return (
      <span dir="ltr" className={className}
        style={{ fontSize: size, fontWeight: weight, color: UI.text.muted, fontFamily: FONT.latin, unicodeBidi: "isolate", ...style }}
        title="אין שער חליפין חי">—</span>
    );
  }
  return <Amount value={(Number(value) || 0) * fx.rate} currency={cur} size={size} weight={weight} color={color}
    className={className} style={style} whole={whole} signed={signed} decimals={decimals}
    {...(letterSpacing ? { letterSpacing } : null)}/>;
}

// ─── A sum at the scale of a company's report: "$1.2B", "$350M" ──────────────
// Dollars as reported, not converted (a filing is in dollars). Through Amount,
// so the sign and the "$" follow the same rule as every other figure.
export function compactParts(n) {
  const abs = Math.abs(n);
  if (abs >= 1e12) return { value: n / 1e12, decimals: 2, suffix: "T" };
  if (abs >= 1e9) return { value: n / 1e9, decimals: abs >= 1e11 ? 0 : 1, suffix: "B" };
  if (abs >= 1e6) return { value: n / 1e6, decimals: abs >= 1e8 ? 0 : 1, suffix: "M" };
  if (abs >= 1e3) return { value: n / 1e3, decimals: 0, suffix: "K" };
  return { value: n, decimals: 0, suffix: null };
}
export function CompactMoney({ value, size = 14, weight = 600, color, signed = false }) {
  if (value == null || !Number.isFinite(Number(value))) return <span>—</span>;
  const c = compactParts(Number(value));
  return <Amount value={c.value} currency="USD" decimals={c.decimals} suffix={c.suffix} size={size} weight={weight} color={color} signed={signed}/>;
}
